import {combineLatest, Observable} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {DashboardStudy, DashboardUser} from '../../types/dashboard.types';
import {RtPlatformService} from '../../../rt-platform/rt-platform.service';
import {
  BackendViewRegion,
  DashboardViewRegion,
  DashboardViewRegionMriLabel,
} from '../../../dashboard-view-regions/types/dashboard-view-regions.types';

@Injectable()
export class DashboardService {
  constructor(private http: HttpClient, private platform: RtPlatformService) {}

  /**
   * Converts a backend view region to a dashboard view region.
   * @param backendViewRegion - The backend view region to convert.
   * @param mriLabels - A map of MRI labels indexed by their IDs.
   */
  static backendViewRegionToDashboardViewRegion(
    backendViewRegion: BackendViewRegion,
    mriLabels: Record<DashboardViewRegionMriLabel['id'], DashboardViewRegionMriLabel>
  ): DashboardViewRegion {
    // Extract the MRI labels from the backend view region using the provided mriLabels map.
    const mriLabelsList = backendViewRegion.mri_label_ids.map(labelId => mriLabels[labelId]);

    return {
      ...backendViewRegion,
      mri_labels: mriLabelsList,

      // Convert subregions recursively using the same function.
      subregions: backendViewRegion.subregions.map(subregion =>
        DashboardService.backendViewRegionToDashboardViewRegion(subregion, mriLabels)
      ),
    };
  }

  /**
   * Fetches the user data for the given user UID.
   * @param userUid - The UID of the user to fetch data for.
   * @returns An observable containing the user data.
   */
  public getDashboardUser(userUid: DashboardUser['uid']): Observable<DashboardUser> {
    const url = `${this.platform.baseUrl}/api/mock/user-data/${userUid}/`;
    return this.http.get<DashboardUser>(url);
  }

  /**
   * Fetches the specific study for the given study ID.
   * @param studyId - The ID of the study to fetch.
   * @returns An observable containing the study data.
   */
  public getStudyDetails(studyId: DashboardStudy['id']): Observable<DashboardStudy> {
    const studyUrl = `${this.platform.baseUrl}/api/mock/study-detailed/${studyId}/`;
    const mriLabelsUrl = `${this.platform.baseUrl}/api/mock/mri-labels/${studyId}/`;
    return combineLatest([
      this.http.get<DashboardStudy<BackendViewRegion>>(studyUrl),
      this.http.get<Record<DashboardViewRegionMriLabel['id'], DashboardViewRegionMriLabel>>(mriLabelsUrl),
    ]).pipe(
      filter(([study, mriLabels]) => !!study && !!mriLabels),
      map(([study, mriLabels]) => ({
        ...study,
        view_regions: study.view_regions.map(viewRegion => DashboardService.backendViewRegionToDashboardViewRegion(viewRegion, mriLabels)),
      }))
    );
  }
}
